import { useEffect, useId, useRef, useState } from "react";
import { formatMoney } from "../../../core/src";

export function RevenueChart({
  values,
  labels,
  currency = "USD",
}: {
  values: number[];
  labels: string[];
  currency?: string;
}) {
  const gradient = useId().replace(/:/g, "");
  const [hovered, setHovered] = useState<number | null>(null);
  const container = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(740);
  useEffect(() => {
    if (!container.current || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) =>
      setWidth(Math.max(300, Math.min(740, entries[0].contentRect.width))),
    );
    observer.observe(container.current);
    return () => observer.disconnect();
  }, []);
  const max = Math.ceil((Math.max(...values, 1000) * 1.2) / 100) * 100;
  const height = 238,
    left = 40,
    top = 18,
    bottom = 32;
  const axis = new Intl.NumberFormat("en", {
    notation: "compact",
    maximumFractionDigits: 1,
  });
  const points = values.map((v, i) => ({
    x: left + (width - left - 20) * (i / Math.max(1, values.length - 1)),
    y: top + (height - top - bottom) * (1 - v / max),
  }));
  const line = points.map((p, i) => `${i ? "L" : "M"} ${p.x} ${p.y}`).join(" ");
  const selected = hovered === null ? null : points[hovered];
  return (
    <div ref={container} className="revenue-chart">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`Revenue chart: ${labels.map((l, i) => `${l} ${formatMoney(values[i] ?? 0, currency)}`).join(", ")}`}
      >
        <defs>
          <linearGradient id={gradient} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--chart-line)" stopOpacity=".18" />
            <stop offset="100%" stopColor="var(--chart-line)" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0, 1, 2, 3, 4].map((n) => {
          const y = top + ((height - top - bottom) * n) / 4;
          return (
            <g key={n}>
              <line
                x1={left}
                x2={width - 18}
                y1={y}
                y2={y}
                stroke="var(--border)"
                strokeDasharray="3 5"
              />
              <text x={0} y={y + 4} className="chart-label">
                {axis.format(max * (1 - n / 4))}
              </text>
            </g>
          );
        })}
        {points.length > 0 && (
          <>
            <path
              d={`${line} L ${points[points.length - 1].x} ${height - bottom} L ${left} ${height - bottom} Z`}
              fill={`url(#${gradient})`}
            />
            <path
              d={line}
              stroke="var(--chart-line)"
              strokeWidth="3"
              strokeLinejoin="round"
              fill="none"
            />
          </>
        )}
        {points.map((p, i) => (
          <g
            key={i}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
          >
            <rect
              x={p.x - 26}
              y={top}
              width="52"
              height={height - top}
              fill="transparent"
            />
            <circle
              cx={p.x}
              cy={p.y}
              r={hovered === i ? 5 : 3}
              fill="var(--surface)"
              stroke="var(--chart-line)"
              strokeWidth="2"
            />
            <text
              x={p.x}
              y={height - 5}
              textAnchor="middle"
              className="chart-label"
            >
              {width < 450 && labels.length > 6 && i % 2 === 1 ? "" : labels[i]}
            </text>
            <title>
              {labels[i]}: {formatMoney(values[i], currency)}
            </title>
          </g>
        ))}
        {selected && hovered !== null && (
          <g pointerEvents="none">
            <line
              x1={selected.x}
              x2={selected.x}
              y1={top}
              y2={height - bottom}
              stroke="var(--chart-line)"
              strokeDasharray="4 4"
            />
            <rect
              x={Math.max(48, Math.min(selected.x - 55, width - 128))}
              y={Math.max(2, selected.y - 37)}
              width="110"
              height="28"
              rx="6"
              fill="var(--text)"
            />
            <text
              x={Math.max(103, Math.min(selected.x, width - 73))}
              y={Math.max(20, selected.y - 19)}
              textAnchor="middle"
              fill="var(--surface)"
              fontSize="11"
            >
              {formatMoney(values[hovered], currency)}
            </text>
          </g>
        )}
      </svg>
    </div>
  );
}
export function RingChart({
  percent,
  label,
}: {
  percent: number;
  label: string;
}) {
  const value = Math.max(0, Math.min(100, percent));
  return (
    <div className="ring-chart">
      <svg viewBox="0 0 180 180" role="img" aria-label={`${label}: ${value}%`}>
        <circle
          cx="90"
          cy="90"
          r="72"
          fill="none"
          stroke="var(--subtle)"
          strokeWidth="14"
        />
        <circle
          cx="90"
          cy="90"
          r="72"
          fill="none"
          stroke="var(--chart-line)"
          strokeWidth="14"
          strokeDasharray={`${value * 4.524} 452.4`}
          strokeLinecap="round"
          transform="rotate(-90 90 90)"
        />
      </svg>
      <div>
        <strong>{value}%</strong>
        <span>{label}</span>
      </div>
    </div>
  );
}
